// Copilot analytics engine. Interprets an analytical natural-language question
// against the practice's in-scope data and returns a short grounded answer plus
// a fully-formed ECharts `option` JSON the UI renders dynamically. This stands
// in for an LLM that emits chart specs as a tool-call; the same selectors would
// be exposed to the model in Phase 2.

import { buildWorklist, stediAnalytics, stediTrends, pct, money } from './data';
import { CHARGE_BY_CPT } from '../mock';

const PALETTE = ['#0e7490', '#27b2c6', '#0e92a8', '#8b5cf6', '#0ea5e9', '#10b981', '#f59e0b', '#f43f5e', '#6366f1'];
const AXIS = '#94a3b8';
const GRID = '#eef0f5';

const baseTooltip = { backgroundColor: '#0e1117', borderColor: '#0e1117', textStyle: { color: '#fff', fontSize: 12 }, padding: [6, 10] };

function barOption(rows, { valueFmt, horizontal } = {}) {
  const cats = rows.map((r) => r.label);
  const cat = { type: 'category', data: cats, axisLine: { lineStyle: { color: GRID } }, axisTick: { show: false }, axisLabel: { color: AXIS, fontSize: 11, interval: 0, rotate: cats.length > 5 && !horizontal ? 18 : 0 } };
  const val = { type: 'value', axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { color: GRID } }, axisLabel: { color: AXIS, fontSize: 11 } };
  return {
    grid: { left: horizontal ? 90 : 36, right: 16, top: 16, bottom: horizontal ? 16 : 34 },
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, ...baseTooltip, valueFormatter: valueFmt },
    xAxis: horizontal ? val : cat,
    yAxis: horizontal ? { ...cat, inverse: true } : val,
    series: [{
      type: 'bar', data: rows.map((r, i) => ({ value: r.value, itemStyle: { color: PALETTE[i % PALETTE.length], borderRadius: horizontal ? [0, 5, 5, 0] : [5, 5, 0, 0] } })),
      barMaxWidth: 46, label: { show: true, position: horizontal ? 'right' : 'top', color: '#475569', fontSize: 11, formatter: (p) => (valueFmt ? valueFmt(p.value) : p.value) },
    }],
  };
}

function pieOption(rows) {
  return {
    tooltip: { trigger: 'item', ...baseTooltip, valueFormatter: (v) => `${v}` },
    legend: { bottom: 0, textStyle: { color: '#64748b', fontSize: 11 }, itemWidth: 10, itemHeight: 10 },
    series: [{
      type: 'pie', radius: ['46%', '70%'], center: ['50%', '44%'], avoidLabelOverlap: true,
      itemStyle: { borderColor: '#fff', borderWidth: 2 },
      label: { show: false }, labelLine: { show: false },
      data: rows.map((r, i) => ({ name: r.label, value: r.value, itemStyle: { color: r.color || PALETTE[i % PALETTE.length] } })),
    }],
  };
}

function lineOption(trend, series) {
  return {
    grid: { left: 36, right: 16, top: 22, bottom: 28 },
    tooltip: { trigger: 'axis', ...baseTooltip },
    legend: { top: 0, right: 0, textStyle: { color: '#64748b', fontSize: 11 }, itemWidth: 12, itemHeight: 8 },
    xAxis: { type: 'category', data: trend.map((t) => t.week), boundaryGap: false, axisLine: { lineStyle: { color: GRID } }, axisTick: { show: false }, axisLabel: { color: AXIS, fontSize: 10 } },
    yAxis: { type: 'value', axisLine: { show: false }, splitLine: { lineStyle: { color: GRID } }, axisLabel: { color: AXIS, fontSize: 11 } },
    series: series.map((s, i) => ({
      name: s.name, type: 'line', smooth: true, stack: s.stack, showSymbol: false, lineStyle: { width: 2, color: PALETTE[i % PALETTE.length] },
      areaStyle: s.area ? { color: PALETTE[i % PALETTE.length], opacity: 0.14 } : undefined,
      itemStyle: { color: PALETTE[i % PALETTE.length] }, data: trend.map((t) => t[s.key]),
    })),
  };
}

// Group worklist by a dimension; metric is count or revenue.
const DIMENSIONS = {
  payer: { test: /payer|insurer|insurance|carrier/, label: 'payer', key: (w) => w.payer?.name || 'Unknown' },
  provider: { test: /provider|practitioner|doctor|physician|clinician/, label: 'provider', key: (w) => (w.provider ? `Dr. ${w.provider.lastName}` : 'Unknown') },
  practice: { test: /practice|tenant|clinic|location group|site/, label: 'practice', key: (w) => w.practice?.name || 'Unknown' },
  planType: { test: /plan type|plan-type|plantype|by plan|plan mix|ppo|hmo|epo/, label: 'plan type', key: (w) => w.plan?.planType || w.check?.coverage?.planType || 'Unknown' },
  visitType: { test: /visit type|visit-type|appointment type|by visit|reason/, label: 'visit type', key: (w) => w.visitType || 'Unknown' },
  department: { test: /department|dept|office/, label: 'department', key: (w) => w.location?.name || 'Main' },
};

function groupBy(wl, keyFn, metric) {
  const m = {};
  wl.forEach((w) => {
    const k = keyFn(w);
    (m[k] ||= 0);
    m[k] += metric === 'revenue' ? (CHARGE_BY_CPT[w.apt.cpt] || 150) : 1;
  });
  return Object.entries(m).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
}

// Returns { answer, chart: { kind, title, option }, items } or null if the
// question isn't analytical / chartable.
export function runAnalytics(query, practiceId = 'prac_001') {
  const q = (query || '').toLowerCase().trim();
  if (!q) return null;

  const wantsChart = /(chart|graph|plot|visuali|trend|over time|breakdown|break down|distribution|split|mix|histogram|compare|share of|how many .* by|count .* by|by (payer|provider|practitioner|doctor|practice|plan|visit|risk|network|department))/.test(q);
  const a = stediAnalytics(practiceId);
  const wl = buildWorklist(practiceId);

  // 1) Eligibility volume / trend over time → stacked area line
  if (/over time|trend|per week|weekly|volume|timeline/.test(q)) {
    const trend = stediTrends(practiceId, 12);
    let series, title, answer;
    if (/auto.?clear/.test(q)) {
      series = [{ name: 'Auto-clear %', key: 'autoPct', area: true }];
      title = 'Auto-clear rate over time'; answer = `Auto-clear rate is trending around ${pct(a.autoClearRate)} across the last 12 weeks.`;
    } else if (/out.?of.?network|oon/.test(q)) {
      series = [{ name: 'Out-of-network %', key: 'oonPct', area: true }];
      title = 'Out-of-network rate over time'; answer = `Out-of-network responses are running near ${pct(a.netPct.out)}.`;
    } else if (/collect|revenue|\$|dollar|money/.test(q)) {
      series = [{ name: 'Flagged to collect ($k)', key: 'collected', area: true }];
      title = 'Flagged to collect over time'; answer = 'Weekly patient responsibility flagged to collect upfront.';
    } else {
      series = [{ name: 'Auto-cleared', key: 'cleared', stack: 'v', area: true }, { name: 'Needs attention', key: 'attention', stack: 'v', area: true }];
      title = 'Eligibility volume over time'; answer = `Eligibility volume over the last 12 weeks — auto-cleared vs. needs-attention. ${pct(a.autoClearRate)} clear without a human.`;
    }
    return { answer, chart: { kind: 'line', title, option: lineOption(trend, series) }, items: [] };
  }

  // 2) Network mix → pie
  if (/network mix|in.?network vs|in vs out|network split|network breakdown|out.?of.?network share/.test(q) || (/network/.test(q) && wantsChart && !/by /.test(q))) {
    const rows = [
      { label: 'In-network', value: a.net.in, color: '#10b981' },
      { label: 'Out-of-network', value: a.net.out, color: '#f43f5e' },
      { label: 'Unknown', value: a.net.unknown, color: '#f59e0b' },
    ].filter((r) => r.value > 0);
    return { answer: `Network mix across ${a.total} responses: ${pct(a.netPct.in)} in-network, ${pct(a.netPct.out)} out-of-network, ${pct(a.netPct.unknown)} unknown.`, chart: { kind: 'pie', title: 'Network mix', option: pieOption(rows) }, items: [] };
  }

  // 3) Risk distribution → pie
  if (/risk/.test(q) && wantsChart) {
    const order = [['high', '#f43f5e'], ['medium', '#f59e0b'], ['low', '#10b981'], ['clear', '#0e7490']];
    const rows = order.map(([lvl, color]) => ({ label: lvl[0].toUpperCase() + lvl.slice(1), value: wl.filter((w) => w.risk.level === lvl).length, color })).filter((r) => r.value > 0);
    return { answer: `Risk distribution across ${wl.length} appointments in scope.`, chart: { kind: 'pie', title: 'Risk distribution', option: pieOption(rows) }, items: [] };
  }

  // 4) Plan types → bar
  if (/plan type|plan mix|plans returned|by plan/.test(q)) {
    const rows = Object.entries(a.planTypes).map(([label, value]) => ({ label, value })).sort((x, y) => y.value - x.value);
    return { answer: `Plan types returned across ${a.total} responses. ${rows[0]?.label} is the most common (${rows[0]?.value}).`, chart: { kind: 'bar', title: 'Plan types returned', option: barOption(rows) }, items: [] };
  }

  // 5) Out-of-network rate by payer → horizontal bar
  if (/out.?of.?network|oon/.test(q) && /payer|by /.test(q)) {
    const rows = a.byPayer.map((p) => ({ label: p.name, value: Math.round(p.oonRate * 100) })).sort((x, y) => y.value - x.value);
    return { answer: `Out-of-network rate by payer. ${rows[0]?.label} is highest at ${rows[0]?.value}%.`, chart: { kind: 'bar', title: 'Out-of-network rate by payer', option: barOption(rows, { horizontal: true, valueFmt: (v) => `${v}%` }) }, items: [] };
  }

  // 6) Generic "<metric> by <dimension>" — count or revenue grouped by a field
  const dim = Object.values(DIMENSIONS).find((d) => d.test.test(q));
  if (dim || wantsChart) {
    const d = dim || DIMENSIONS.payer; // default dimension
    const metric = /revenue|collection|charge|projected|\$|dollar|money|billed/.test(q) ? 'revenue' : 'count';
    const rows = groupBy(wl, d.key, metric);
    const horizontal = rows.length > 5 || ['provider', 'practice', 'payer'].includes(Object.keys(DIMENSIONS).find((k) => DIMENSIONS[k] === d));
    const valueFmt = metric === 'revenue' ? (v) => money(v) : undefined;
    const title = `${metric === 'revenue' ? 'Projected revenue' : 'Appointments'} by ${d.label}`;
    const top = rows[0];
    const answer = top ? `${title}: ${top.label} leads with ${metric === 'revenue' ? money(top.value) : `${top.value} appointment${top.value === 1 ? '' : 's'}`} across ${rows.length} ${d.label}${rows.length === 1 ? '' : 's'}.` : 'No data in scope.';
    return { answer, chart: { kind: 'bar', title, option: barOption(rows, { horizontal, valueFmt }) }, items: [] };
  }

  return null;
}
